import { Box } from '@chakra-ui/react';
import { ChevronDown } from 'lucide-react';
import { Accordion, Text, VStack } from 'components/ui';
import { Affiliate } from '../../models/affiliate';
import {
  COMMON_QUESTIONS_LABEL,
  QUESTIONS,
  RESCHEDULE_ANSWER,
} from './schedulerCopy';
import { AvailabilityType } from './useFetchAvailability';
import {
  CostAnswerKind,
  getCareVariant,
  getCostAnswer,
  getVisitCopy,
} from './useSchedulerFaq';

type Props = {
  affiliate?: Affiliate;
  availabilityType: AvailabilityType;
  isStripe?: boolean;
};

const ANSWER_COLOR = 'secondary.200';

const Answer = ({ children }: { children: React.ReactNode }) => (
  <Box color={ANSWER_COLOR} fontSize={'13.5px'} lineHeight={'1.55'}>
    {children}
  </Box>
);

const P = ({
  children,
  ...rest
}: { children: React.ReactNode } & Record<string, unknown>) => (
  <Text color={ANSWER_COLOR} {...rest}>
    {children}
  </Text>
);

const Lead = ({ children }: { children: React.ReactNode }) => (
  <Text color={'secondary.500'} fontSize={'14.5px'} fontWeight={700}>
    {children}
  </Text>
);

/**
 * Emphasis in the default cost copy is written as **bold** so the strings stay
 * readable in one place rather than being split into JSX fragments.
 */
const withEmphasis = (text: string) =>
  text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? (
      <Text as={'span'} color={'secondary.500'} fontWeight={700} key={i}>
        {part}
      </Text>
    ) : (
      part
    )
  );

export const CommonQuestions = ({
  affiliate,
  availabilityType,
  isStripe,
}: Props) => {
  const variant = getCareVariant(availabilityType);
  const visit = getVisitCopy(variant);
  const cost = getCostAnswer({ affiliate, isStripe });

  const items: { answer: React.ReactNode; question: string }[] = [
    {
      question: QUESTIONS.whatDoIGet,
      answer: (
        <Answer>
          <P>{visit.whatDoIGet.intro}</P>

          <Text color={'secondary.500'} fontWeight={700} mt={'9px'}>
            {visit.whatDoIGet.includedLabel}
          </Text>

          <Box as={'ul'} mt={'8px'} pl={'20px'} listStyleType={'disc'}>
            {visit.whatDoIGet.included.map((line) => (
              <Box as={'li'} key={line} lineHeight={'1.5'} mb={'5px'}>
                {line}
              </Box>
            ))}
          </Box>
        </Answer>
      ),
    },
  ];

  if (cost.kind !== CostAnswerKind.hidden) {
    items.push({
      question: QUESTIONS.cost,
      answer: (
        <Answer>
          {cost.kind === CostAnswerKind.affiliate && <P>{cost.body}</P>}

          {cost.kind === CostAnswerKind.stripe && (
            <>
              <Lead>{cost.lead}</Lead>
              {cost.paragraphs.map((p) => (
                <P key={p} mt={'6px'}>
                  {p}
                </P>
              ))}
            </>
          )}

          {cost.kind === CostAnswerKind.default &&
            cost.paragraphs.map((p, i) => (
              <P key={p} mt={i ? '9px' : 0}>
                {withEmphasis(p)}
              </P>
            ))}
        </Answer>
      ),
    });
  }

  items.push(
    {
      question: QUESTIONS.whatHappens,
      answer: <Answer>{visit.whatHappens}</Answer>,
    },
    {
      question: QUESTIONS.reschedule,
      answer: <Answer>{RESCHEDULE_ANSWER}</Answer>,
    }
  );

  return (
    <VStack alignItems={'stretch'} gap={'10px'} mt={'22px'} w={'100%'}>
      <Text
        color={'blueGray.600'}
        fontSize={'12.5px'}
        fontWeight={700}
        letterSpacing={'0.04em'}
        mb={'4px'}
        textAlign={'left'}
        textTransform={'uppercase'}
      >
        {COMMON_QUESTIONS_LABEL}
      </Text>

      <Accordion allowMultiple w={'100%'}>
        {items.map((item, i) => (
          <Accordion.Item index={i} key={item.question}>
            <Box
              bg={'white'}
              borderColor={'fieldBorder.200'}
              borderWidth={1}
              mb={'10px'}
              rounded={'row'}
            >
              <Accordion.Summary
                alignItems={'center'}
                cursor={'pointer'}
                display={'flex'}
                gap={'12px'}
                justifyContent={'space-between'}
                px={'18px'}
                py={'15px'}
                w={'100%'}
              >
                <Text
                  color={'secondary.500'}
                  fontSize={'14.5px'}
                  fontWeight={600}
                  textAlign={'left'}
                >
                  {item.question}
                </Text>

                <Accordion.Icon color={'secondary.200'} flexShrink={0}>
                  <ChevronDown size={14} />
                </Accordion.Icon>
              </Accordion.Summary>

              <Accordion.Details pb={'15px'} px={'18px'}>
                {item.answer}
              </Accordion.Details>
            </Box>
          </Accordion.Item>
        ))}
      </Accordion>
    </VStack>
  );
};
